import { v4 as uuid } from 'uuid';
import { errorParser } from 'src/app/utils/errorParser';
import getQueryParam from '../utils/getQueryParam';

type QueryParamsType = {
  [key: string]: string | number | boolean;
};

const HTTP_HEADERS = {
  CONTENT_JSON: {
    'Content-Type': 'application/json',
    'X-Correlation-ID': getQueryParam('correlation_id') ?? uuid(),
  },
};

type NetworkServiceReturnType<T> = {
  status: boolean;
  data: T | null;
  error: any | null;
};

/**
 * Network service class to gracefully handle errors and return them to UI components.
 */
class NetworkService {
  private static instance: NetworkService;

  public constructor() {
    NetworkService.instance = this;
  }

  /**
   * Gets a shared instance of the NetworkService
   */
  static getInstance() {
    if (NetworkService.instance) {
      return NetworkService.instance;
    }

    return new NetworkService();
  }

  /**
   * Post Request
   * @param endpointURL the url to send request to
   * @param body the json payload to send along with request
   * @param headers the headers to set on request, ex: auth token
   * @returns api response of type passed by callee
   */
  public async post<T>(
    endpointURL: string,
    body: BodyInit | null | undefined,
    headers?: HeadersInit
  ): Promise<NetworkServiceReturnType<T>> {
    return await fetch(endpointURL, {
      headers: {
        ...HTTP_HEADERS.CONTENT_JSON,
        ...headers,
      },
      method: 'POST',
      body,
    })
      .then((response) => this.validateResponse(response))
      .then((response) => ({
        status: true,
        data: response as T,
        error: null,
      }))
      .catch((error) => ({
        status: false,
        data: null,
        error: error.message,
      }));
  }

  /**
   * Get Request
   * @param endpointURL the url to send request to
   * @param queryParams the key-value paired object to pass as queryparam
   * @param headers the headers to set on request, ex: auth token
   * @returns api response of type passed by callee
   */
  public async get<T>(
    endpointURL: string,
    queryParams: QueryParamsType,
    headers?: HeadersInit
  ): Promise<NetworkServiceReturnType<T>> {
    return await fetch(endpointURL + '?' + new URLSearchParams(queryParams as any).toString(), {
      headers: {
        ...HTTP_HEADERS.CONTENT_JSON,
        ...headers,
      },
      method: 'GET',
    })
      .then((response) => this.validateResponse(response))
      .then((response) => ({
        status: true,
        data: response as T,
        error: null,
      }))
      .catch((error) => ({
        status: false,
        data: null,
        error: error.message,
      }));
  }

  /**
   * Validate the API response if it was success or has errors
   * @param response HTTP response
   * @returns response or throws errors
   */
  protected async validateResponse(response: Response) {
    if (!response.ok) {
      return this.parseError(response);
    } else {
      return response.json();
    }
  }

  /**
   * Parse the error from stream
   * @param response HTTP Response
   */
  protected async parseError(response: Response) {
    await response.text().then((readableResponse) => {
      throw new Error(errorParser(readableResponse));
    });
  }
}
const networkService = NetworkService.getInstance();

export default networkService;
